import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { FileText, Mail, ShieldCheck, Users, HardHat } from "lucide-react";
import { Header } from "@/components/Header";
import { KineticLines, Reveal } from "@/components/KineticLines";
import { TextMarquee } from "@/components/Marquee";
import { Chapters } from "@/components/Chapters";
import { Gallery } from "@/components/Gallery";
import { QuoteForm } from "@/components/QuoteForm";
import { Footer } from "@/components/Footer";

const CHAPTERS = [
  {
    num: "01",
    title: "MULTI-FAMILY & MIXED USE",
    text: "Apartment complexes, townhome communities and mixed-use buildings. We staff to your schedule and phase the work so tenants and operations are never disrupted.",
    image: "https://images.pexels.com/photos/10486073/pexels-photo-10486073.jpeg?auto=compress&cs=tinysrgb&w=1920&q=85",
  },
  {
    num: "02",
    title: "NEW CONSTRUCTION",
    text: "From takeoff to punch list. We bid from your drawings, hit our milestones, and keep your supers updated with photo documentation at every stage.",
    image: "https://images.unsplash.com/photo-1587582423116-ec07293f0395?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxob3VzZSUyMGNvbnN0cnVjdGlvbiUyMGZyYW1pbmd8ZW58MHx8fHwxNzkwMjEzNTIzfDA&ixlib=rb-4.1.0&q=85",
  },
  {
    num: "03",
    title: "RE-CLADS & RENOVATIONS",
    text: "Building envelope refreshes, failed-siding remediation and full re-clads. We work around occupied buildings and coordinate directly with your PM.",
    image: "https://images.pexels.com/photos/34007142/pexels-photo-34007142.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    num: "04",
    title: "CUSTOM METAL PANS & WATERPROOFING",
    text: "Custom-made door and window pans in any gauge of aluminum or galvanized metal, fabricated to fit your openings. Plus window caulking and full waterproofing details that keep the building envelope tight.",
    image: "https://images.pexels.com/photos/6124242/pexels-photo-6124242.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    num: "05",
    title: "BUILDING MAINTENANCE",
    text: "Keep your properties looking sharp and staying sealed: siding repairs, pressure washing and window cleaning — scheduled maintenance programs or one-time service calls.",
    image: "https://images.unsplash.com/photo-1786295866836-2949bbd847cd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxwcmVzc3VyZSUyMHdhc2hpbmclMjBob3VzZXxlbnwwfHx8fDE3OTAyMTM1MjN8MA&ixlib=rb-4.1.0&q=85",
  },
];

const GC_POINTS = [
  { icon: FileText, title: "Fast, Detailed Bids", text: "Send drawings, get a line-item proposal with alternates and lead times." },
  { icon: ShieldCheck, title: "Fully Insured", text: "COIs, W-9 and safety docs delivered before we step on site." },
  { icon: Users, title: "Own Crews, No Brokers", text: "The team that bids your job is the team that installs it." },
  { icon: HardHat, title: "Labor-Only Subcontracts", text: "Already have materials covered? We take subcontracts for labor only — skilled siding crews, ready to mobilize." },
];

export default function Commercial() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <div data-testid="commercial-page" className="bg-background relative">
      <Header />

      <section ref={heroRef} data-testid="commercial-hero" className="relative min-h-[92vh] flex items-end overflow-hidden grain">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/10486073/pexels-photo-10486073.jpeg?auto=compress&cs=tinysrgb&w=1920&q=85"
            alt="Commercial building facade"
            className="w-full h-full object-cover scale-110"
          />
        </motion.div>
        <div className="absolute inset-0 bg-[#131510]/45" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 pb-16 md:pb-24 w-full">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="text-primary text-xs font-bold tracking-[0.35em] uppercase mb-5"
          >
            Commercial Siding · For General Contractors
          </motion.p>
          <KineticLines
            lines={["BUILT FOR", "THE GCs WHO", "BUILD BIG."]}
            className="font-display text-6xl sm:text-8xl lg:text-9xl leading-[0.88] tracking-wide"
          />
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-8 flex flex-wrap items-center gap-5"
          >
            <a
              href="mailto:yali@infinitesiding.com?subject=Drawings%20for%20Bid%20—%20Commercial%20Project"
              data-testid="hero-send-drawings-cta"
              className="rounded-lg bg-primary text-primary-foreground px-8 py-4 font-bold uppercase tracking-widest text-sm transition-transform duration-300 hover:-translate-y-1 hover:shadow-[0_12px_36px_rgba(193,214,46,0.35)]"
            >
              Send Us Your Drawings
            </a>
            <span className="text-white/60 text-sm">Multi-family · New construction · Re-clads · Metal pans & waterproofing · Maintenance</span>
          </motion.div>
        </div>
      </section>

      <TextMarquee items={["COMMERCIAL SIDING", "MULTI-FAMILY", "NEW CONSTRUCTION", "RE-CLADS", "METAL PANS & WATERPROOFING", "BUILDING MAINTENANCE", "NY · CT · MA"]} />

      <section data-testid="drawings-section" className="py-24 md:py-32 px-5 md:px-10 border-b border-white/10 bg-card">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="text-primary text-xs font-bold tracking-[0.3em] uppercase mb-3">For General Contractors</p>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.9]">SEND YOUR<br /><span className="text-primary">DRAWINGS</span></h2>
            <p className="mt-6 text-white/60 text-sm md:text-base leading-relaxed max-w-md">
              Email your plans and specs directly to our estimating team. We turn around detailed, line-item proposals — with alternates, lead times and manpower plans. We also take subcontracts for labor only, so you can keep your bid schedule.
            </p>
            <a
              href="mailto:yali@infinitesiding.com?subject=Drawings%20for%20Bid%20—%20Commercial%20Project"
              data-testid="drawings-email-link"
              className="mt-8 inline-flex items-center gap-3 border border-primary rounded-lg text-primary px-6 py-4 font-bold text-sm tracking-widest uppercase transition-colors duration-300 hover:bg-primary hover:text-primary-foreground"
            >
              <Mail size={17} aria-hidden="true" /> yali@infinitesiding.com
            </a>
          </Reveal>
          <div className="space-y-4">
            {GC_POINTS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.1}>
                <div data-testid={`gc-point-${i}`} className="flex gap-5 border border-white/15 bg-card/60 rounded-xl shadow-sm p-6 items-start transition-colors duration-500 hover:border-primary/40">
                  <p.icon size={26} className="text-primary shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <h3 className="font-display text-2xl tracking-wide">{p.title}</h3>
                    <p className="text-white/55 text-sm mt-1 leading-relaxed">{p.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Chapters items={CHAPTERS} />
      <Gallery category="commercial" />
      <QuoteForm variant="commercial" />
      <Footer />
    </div>
  );
}
